import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Link } from 'react-router-dom';

const HeroSection = () => {
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  
  const rotatingTexts = [
    'Maximaliseer uw subsidie',
    'Bespaar tot 30% op uw investering',
    'Profiteer van fiscale voordelen'
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTextIndex((prev) => (prev + 1) % rotatingTexts?.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [rotatingTexts?.length]);

  return (
    <section className="relative bg-gradient-to-br from-purple-50 via-pink-50 to-purple-100 overflow-hidden py-16 sm:py-20 lg:py-24">
      {/* Animated Background Blobs */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-purple-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-pink-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-0 left-1/2 w-96 h-96 bg-purple-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-purple-100 rounded-full mb-6"
          >
            <Icon name="Gift" size={20} className="text-purple-600" />
            <span className="text-sm font-medium text-purple-700">Subsidies & Regelingen</span>
          </motion.div>

          {/* Animated 3D Title */}
          <motion.h1
            initial={{ opacity: 0, scale: 0.9, rotateX: -20 }}
            animate={{ opacity: 1, scale: 1, rotateX: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-foreground mb-6"
            style={{
              transformStyle: 'preserve-3d',
              textShadow: '0 4px 12px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.08)'
            }}
          >
            <motion.span
              animate={{
                textShadow: [
                  '0 4px 12px rgba(0, 0, 0, 0.1)',
                  '0 8px 24px rgba(168, 85, 247, 0.2)',
                  '0 4px 12px rgba(0, 0, 0, 0.1)'
                ]
              }}
              transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
            >
              Subsidies & Regelingen
            </motion.span>
          </motion.h1>

          {/* Rotating Subtitle */}
          <div className="h-16 mb-8 flex items-center justify-center">
            <AnimatePresence mode="wait">
              <motion.p
                key={currentTextIndex}
                initial={{ opacity: 0, y: 20, rotateX: -45 }}
                animate={{ opacity: 1, y: 0, rotateX: 0 }}
                exit={{ opacity: 0, y: -20, rotateX: 45 }}
                transition={{ duration: 0.6 }}
                className="text-xl sm:text-2xl text-muted-foreground font-semibold"
                style={{ transformStyle: 'preserve-3d' }}
              >
                {rotatingTexts?.[currentTextIndex]}
              </motion.p>
            </AnimatePresence>
          </div>

          {/* Key Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl mx-auto mb-10"
          >
            <motion.div
              whileHover={{ scale: 1.05, y: -5 }}
              className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-elevation-2"
            >
              <div className="w-12 h-12 bg-purple-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Euro" size={24} className="text-purple-600" />
              </div>
              <p className="text-3xl font-bold text-foreground mb-2">€3.000+</p>
              <p className="text-sm text-muted-foreground">Gemiddelde subsidie ISDE</p>
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.05, y: -5 }}
              className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-elevation-2"
            >
              <div className="w-12 h-12 bg-pink-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Percent" size={24} className="text-pink-600" />
              </div>
              <p className="text-3xl font-bold text-foreground mb-2">21% BTW</p>
              <p className="text-sm text-muted-foreground">Terug bij zonnepanelen</p>
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.05, y: -5 }}
              className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-elevation-2"
            >
              <div className="w-12 h-12 bg-purple-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="MapPin" size={24} className="text-purple-600" />
              </div>
              <p className="text-3xl font-bold text-foreground mb-2">100+</p>
              <p className="text-sm text-muted-foreground">Lokale regelingen</p>
            </motion.div>
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link to="/quote-request-form">
              <Button size="lg" iconName="Search" iconPosition="left">
                Vind uw subsidies
              </Button>
            </Link>
            <Button size="lg" variant="outline" iconName="Download" iconPosition="left">
              Download subsidiegids
            </Button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;